/**
 * SubunitRenderer
 * Renders subunits using InstancedMesh with BoxGeometry.
 * All subunits are rectangular (axis-aligned), no rotation.
 * Supports per-instance color, opacity, and elevation for mezzanine display.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { Subunit } from '@/types/subunit'
import { SUBUNIT_CATEGORY_COLORS, isMezzanine } from '@/types/subunit'

const MAX_INSTANCES = 50
const Z_FIGHTING_OFFSET = 0.005 // meters, lifted above floor

// Shared geometry (unit-sized, scaled per instance via matrix)
const BOX_GEOMETRY = new THREE.BoxGeometry(1, 1, 1)

// Materials
const SUBUNIT_MATERIAL = new THREE.MeshStandardMaterial({
  roughness: 0.5,
  metalness: 0.05,
  transparent: true,
})

// Temp objects for matrix composition
const _position = new THREE.Vector3()
const _quaternion = new THREE.Quaternion()
const _scale = new THREE.Vector3()
const _matrix = new THREE.Matrix4()
const _color = new THREE.Color()

export interface SubunitRenderData {
  subunits: Subunit[]
  /** Parent unit offset from building origin in feet [x, y] */
  parentUnitOffset: [number, number]
}

/**
 * Maps instance index to subunit ID for raycasting/selection
 */
export type SubunitInstanceMap = Map<number, string>

export class SubunitRenderer {
  private group: THREE.Group
  private mesh: THREE.InstancedMesh
  private instanceMap: SubunitInstanceMap = new Map()

  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'subunits'

    this.mesh = new THREE.InstancedMesh(
      BOX_GEOMETRY,
      SUBUNIT_MATERIAL.clone(),
      MAX_INSTANCES
    )
    this.mesh.name = 'subunit-boxes'
    this.mesh.count = 0
    this.mesh.frustumCulled = false

    this.group.add(this.mesh)
  }

  /** Get the Three.js group containing all subunit meshes */
  getGroup(): THREE.Group {
    return this.group
  }

  /** Get instance map for hit testing */
  getInstanceMap(): SubunitInstanceMap {
    return this.instanceMap
  }

  /**
   * Update all subunit instances.
   * Called when subunits change (CRUD) or building loads.
   */
  updateSubunits(renderData: SubunitRenderData[]): void {
    this.instanceMap.clear()

    let index = 0

    for (const { subunits, parentUnitOffset } of renderData) {
      for (const subunit of subunits) {
        if (index >= MAX_INSTANCES) break

        this.setBoxInstance(index, subunit, parentUnitOffset)
        this.instanceMap.set(index, subunit.id)
        index++
      }
    }

    this.mesh.count = index
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }

  /**
   * Set a subunit box instance
   */
  private setBoxInstance(
    index: number,
    subunit: Subunit,
    parentUnitOffset: [number, number],
  ): void {
    const widthM = subunit.width * FEET_TO_METERS
    const depthM = subunit.depth * FEET_TO_METERS
    const heightM = subunit.height * FEET_TO_METERS
    const elevationM = subunit.elevationFt * FEET_TO_METERS

    // Position: parent-unit-local feet → scene meters
    // DB: offsetX → +X, offsetY → -Z (north), height → +Y
    const posX = (parentUnitOffset[0] + subunit.offsetX) * FEET_TO_METERS
    const posY = elevationM + heightM / 2 + Z_FIGHTING_OFFSET
    const posZ = -(parentUnitOffset[1] + subunit.offsetY) * FEET_TO_METERS

    // No rotation (axis-aligned per spec)
    _quaternion.identity()

    _position.set(posX, posY, posZ)
    _scale.set(widthM, heightM, depthM)
    _matrix.compose(_position, _quaternion, _scale)

    this.mesh.setMatrixAt(index, _matrix)

    // Color based on category
    const categoryColor = SUBUNIT_CATEGORY_COLORS[subunit.category] ?? '#808080'
    _color.set(categoryColor)
    this.mesh.setColorAt(index, _color)

    // Adjust opacity for mezzanine (slightly transparent to see through)
    const material = this.mesh.material as THREE.MeshStandardMaterial
    material.opacity = isMezzanine(subunit) ? 0.7 : 0.85
  }

  /**
   * Find subunit ID from a raycast intersection
   */
  getSubunitIdFromIntersection(intersection: THREE.Intersection): string | null {
    const instanceId = intersection.instanceId
    if (instanceId == null) return null
    if (intersection.object === this.mesh) {
      return this.instanceMap.get(instanceId) ?? null
    }
    return null
  }

  /**
   * Get raycastable meshes for hit testing
   */
  getRaycastTargets(): THREE.Object3D[] {
    return [this.mesh]
  }

  /**
   * Dispose all GPU resources
   */
  dispose(): void {
    this.mesh.dispose()
    ;(this.mesh.material as THREE.Material).dispose()
    this.group.clear()
  }
}
